import type * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { Skeleton } from '@nula/core-ui';
import { CanvasEngine } from '@/features/canvas/engine/surfaces/canvas-engine';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import type { YjsRoomKind } from '@/lib/sync/yjs-registry';
import { type CanvasBinding } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import styles from './embeddable-canvas.module.css';

/**
 * Engine props EmbeddableCanvas owns / overrides itself — callers supply the
 * rest of the engine surface (awareness wiring, viewport persistence, drawing
 * overlays, in-flow helpers, …) verbatim and EmbeddableCanvas forwards them.
 */
type OwnedEngineProps = 'doc' | 'awareness' | 'clientId' | 'binding';

type ForwardedEngineProps = Omit<
  React.ComponentProps<typeof CanvasEngine>,
  OwnedEngineProps
>;

/** Doc-mode: caller already has a resolved Yjs doc + awareness. */
interface DocModeProps {
  doc: Y.Doc;
  awareness: Awareness | null;
  entityId?: never;
  entityKindHint?: never;
}

/** Entity-mode: EmbeddableCanvas resolves the doc itself via useEntityYjs. */
interface EntityModeProps {
  entityId: string;
  entityKindHint?: YjsRoomKind;
  doc?: never;
  awareness?: never;
}

interface CommonProps {
  /**
   * Where the canvas's Yjs roots live in the doc. Defaults to top-level
   * (standalone canvas — byte-identical to legacy inline access). A nested
   * binding embeds the canvas under a parent map (e.g. a document surface diagram).
   */
  binding?: CanvasBinding;
}

type EmbeddableCanvasProps = (DocModeProps | EntityModeProps) &
  CommonProps &
  ForwardedEngineProps;

/**
 * The canvas embedding boundary. Mounts `CanvasEngine` against a Yjs doc —
 * supplied directly (doc-mode) or resolved by `entityId` (entity-mode) — under
 * the given `binding` (top-level by default, or nested for an embedded canvas).
 *
 * It owns exactly three concerns: doc resolution, a loading skeleton while the
 * entity room syncs, and seeding the engine's `binding` + `clientId`. Every
 * other engine prop (awareness wiring, viewport persistence, drawing overlays,
 * in-flow helpers, …) is forwarded verbatim, so a consumer keeps full control
 * of the canvas surface without re-implementing the mount.
 *
 * No workspace-tab or route assumptions — any feature can drop one in.
 */
export function EmbeddableCanvas(props: EmbeddableCanvasProps) {
  if (props.entityId !== undefined) {
    const { entityId, entityKindHint, ...rest } = props;
    return (
      <EntityResolvedCanvas
        entityId={entityId}
        entityKindHint={entityKindHint}
        {...rest}
      />
    );
  }

  const { doc, awareness, ...rest } = props;
  return <MountedCanvas doc={doc} awareness={awareness} {...rest} />;
}

interface EntityResolvedCanvasProps extends CommonProps, ForwardedEngineProps {
  entityId: string;
  entityKindHint?: YjsRoomKind;
}

function EntityResolvedCanvas({
  entityId,
  entityKindHint,
  ...rest
}: EntityResolvedCanvasProps) {
  const { doc, awareness } = useEntityYjs(entityId, entityKindHint);

  if (!doc) {
    return <Skeleton active paragraph={{ rows: 3 }} className={styles.skeleton} />;
  }

  return <MountedCanvas doc={doc} awareness={awareness} {...rest} />;
}

interface MountedCanvasProps extends CommonProps, ForwardedEngineProps {
  doc: Y.Doc;
  awareness: Awareness | null;
}

/**
 * Mounts `CanvasEngine` against an already-resolved doc — the single engine
 * mount shared by both doc-mode and entity-mode. Seeds the binding + `clientId`;
 * forwards every other engine prop verbatim.
 *
 * Binding resolution is the single source of truth: an explicit `binding` prop
 * wins, otherwise we fall back to the surrounding `CanvasBindingContext` value.
 * That context defaults to `TOP_LEVEL_BINDING`, so a standalone canvas with no
 * provider above it stays byte-identical. The win is that any consumer rendering
 * inside a binding context (e.g. the editor, which reads the same context for
 * its data ops) gets a render layer that agrees with its data layer — no
 * split-brain where data reads nested roots while the engine renders top-level.
 */
function MountedCanvas({ doc, awareness, binding, ...engineProps }: MountedCanvasProps) {
  const contextBinding = useCanvasBinding();
  return (
    <CanvasEngine
      doc={doc}
      awareness={awareness}
      clientId={doc.clientID}
      binding={binding ?? contextBinding}
      {...engineProps}
    />
  );
}
